// API-Service für Backend-Kommunikation
// In Docker verwendet Nginx einen Proxy: /api -> backend:3000
import { getSessionToken, logout } from './authService';
import { buildImageUrl, isImageCached, canLoadRemotePartImages } from './imageCache';
import { initDatabase } from './dbService';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const IS_DEV = import.meta.env.DEV;

/**
 * Safe JSON parser to prevent crashes on non-JSON / empty responses (e.g. WebKit/Safari)
 */
async function safeJson(response) {
  try {
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (err) {
    console.warn('Response is not valid JSON:', err);
    return {};
  }
}

class ApiService {
  constructor() {
    this.API_BASE_URL = API_BASE_URL;
  }

  async fetchWithTimeout(url, options = {}, timeout = 15000) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(id);
      
      // Check for configuration errors
      if (response.status === 503) {
        const data = await safeJson(response);
        if (data.error === 'CONFIGURATION_ERROR' && data.fatal) {
          this.showConfigurationError(data);
          throw new Error('CONFIGURATION_ERROR');
        }
        // Non-fatal 503: throw with the actual error message from server
        const err = new Error(data.error || data.message || 'HTTP 503');
        err.status = 503;
        throw err;
      }

      // Automatisches Logout bei 401 oder deaktiviertem User (403 mit USER_DEACTIVATED)
      if (response.status === 401) {
        try {
          await logout();
        } catch {}
      }
      
      if (response.status === 403) {
        try {
          const data = await safeJson(response);
          if (data.code === 'USER_DEACTIVATED') {
            await logout();
            // Zeige Benachrichtigung
            if (window.location.pathname !== '/login') {
              window.location.href = '/login?deactivated=true';
            }
          }
          // Werfe Fehler mit Original-Response weiter
          const error = new Error(data.error || 'Forbidden');
          error.response = response;
          throw error;
        } catch (e) {
          if (e.response) throw e;
          // JSON-Parse-Fehler ignorieren
        }
      }
      
      return response;
    } catch (error) {
      clearTimeout(id);
      throw error;
    }
  }

  showConfigurationError(errorData) {
    // Create persistent error overlay
    const existingOverlay = document.getElementById('config-error-overlay');
    if (existingOverlay) return; // Already shown

    const overlay = document.createElement('div');
    overlay.id = 'config-error-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.95);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    `;

    const errorBox = document.createElement('div');
    errorBox.style.cssText = `
      background: #fff;
      padding: 40px;
      border-radius: 12px;
      max-width: 600px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    `;

    const title = document.createElement('h2');
    title.textContent = '⚠️ Konfigurationsfehler';
    title.style.cssText = 'color: #d32f2f; margin: 0 0 20px 0; font-size: 24px;';

    const message = document.createElement('p');
    message.textContent = errorData.message;
    message.style.cssText = 'color: #333; margin: 0 0 20px 0; font-size: 16px;';

    const detailsTitle = document.createElement('p');
    detailsTitle.textContent = 'Bitte konfigurieren Sie folgende Werte in der backend/.env Datei:';
    detailsTitle.style.cssText = 'color: #666; margin: 0 0 10px 0; font-size: 14px; font-weight: bold;';

    const detailsList = document.createElement('ul');
    detailsList.style.cssText = 'color: #d32f2f; margin: 0 0 20px 0; padding-left: 20px;';
    errorData.details.forEach(detail => {
      const li = document.createElement('li');
      li.textContent = detail;
      li.style.cssText = 'margin: 5px 0; font-family: monospace;';
      detailsList.appendChild(li);
    });

    const instruction = document.createElement('p');
    instruction.textContent = 'Die Anwendung kann erst gestartet werden, wenn die Konfiguration vollständig ist. Weitere Informationen finden Sie in der README.md Datei.';
    instruction.style.cssText = 'color: #666; margin: 0; font-size: 14px; line-height: 1.5;';

    errorBox.appendChild(title);
    errorBox.appendChild(message);
    errorBox.appendChild(detailsTitle);
    errorBox.appendChild(detailsList);
    errorBox.appendChild(instruction);
    overlay.appendChild(errorBox);
    document.body.appendChild(overlay);
  }

  async get(endpoint) {
    try {
      // JWT Token für geschützte Endpoints holen
      const token = await getSessionToken();
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        headers
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API GET ${endpoint} failed:`, error);
      throw error;
    }
  }

  async getWithLongTimeout(endpoint, timeout = 30000) {
    try {
      // JWT Token für geschützte Endpoints holen
      const token = await getSessionToken();
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        headers
      }, timeout);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API GET ${endpoint} failed:`, error);
      throw error;
    }
  }

  async post(endpoint, data) {
    try {
      // JWT Token aus authService holen
      const token = await getSessionToken();
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Authorization Header hinzufügen wenn Token vorhanden
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API POST ${endpoint} failed:`, error);
      throw error;
    }
  }

  async postWithLongTimeout(endpoint, data, timeout = 30000) {
    try {
      const token = await getSessionToken();
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      }, timeout);
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API POST ${endpoint} failed:`, error);
      throw error;
    }
  }

  async postMultipart(endpoint, data) {
    try {
      const token = await getSessionToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: data // FormData does not need Content-Type, the browser sets it with boundary
      });
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API POST Multipart ${endpoint} failed:`, error);
      throw error;
    }
  }

  async putMultipart(endpoint, data) {
    try {
      const token = await getSessionToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers,
        body: data
      });
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API PUT Multipart ${endpoint} failed:`, error);
      throw error;
    }
  }

  async put(endpoint, data) {
    try {
      const token = await getSessionToken();
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API PUT ${endpoint} failed:`, error);
      throw error;
    }
  }

  async patch(endpoint, data) {
    try {
      const token = await getSessionToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API PATCH ${endpoint} failed:`, error);
      throw error;
    }
  }

  async delete(endpoint, data) {
    try {
      const token = await getSessionToken();
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const fetchOptions = { method: 'DELETE', headers };
      if (data) {
        headers['Content-Type'] = 'application/json';
        fetchOptions.body = JSON.stringify(data);
      }
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, fetchOptions);
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API DELETE ${endpoint} failed:`, error);
      throw error;
    }
  }

  // Devices
  async getDevices(filters = null) {
    const params = new URLSearchParams();
    if (typeof filters === 'string') {
      params.append('type', filters);
    } else if (filters && typeof filters === 'object') {
      if (filters.type) params.append('type', filters.type);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
      if (filters.customerId) params.append('customerId', filters.customerId);
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.getWithLongTimeout(`/devices${queryString}`);
  }

  async getDevice(id) {
    return this.get(`/devices/${id}`);
  }

  // Categories
  async getCategories() {
    return this.get('/categories');
  }

  // Parts
  async getParts(filters = {}) {
    const params = new URLSearchParams();
    if (filters.deviceId) params.append('deviceId', filters.deviceId);
    if (filters.categoryId) params.append('categoryId', filters.categoryId);
    if (filters.category) params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);
    
    const queryString = params.toString();
    return this.getWithLongTimeout(`/parts${queryString ? '?' + queryString : ''}`);
  }

  // Paperless Document Search
  async searchPaperless(query) {
    if (!query || query.trim().length < 2) return [];
    return this.get(`/paperless/search?q=${encodeURIComponent(query.trim())}`);
  }

  async getKeyTechnicianContact() {
    try {
      return await this.get('/easyhelp/key-technician-contact');
    } catch {
      return { found: false };
    }
  }



  async getPart(id) {
    return this.get(`/parts/${id}`);
  }

  async getPartShippingMethods(partId) {
    return this.get(`/parts/${partId}/shipping-methods`);
  }

  async savePartShippingMethods(partId, shippingMethodIds) {
    return this.post(`/parts/${partId}/shipping-methods`, { shippingMethodIds });
  }


  async createOrder(orderData) {
    // Backend verschickt pro Versandweg E-Mails (teils mit Excel-Anhang) – kann dauern
    return this.postWithLongTimeout('/orders', orderData, 90000);
  }

  // Auf welche Versandwege wird eine Bestellung mit diesen Teilen aufgeteilt?
  async getOrderShippingPreview(partIds) {
    return this.post('/orders/shipping-preview', { partIds });
  }

  // Info
  async getInfo() {
    return this.get('/info');
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.fetchWithTimeout(`${API_BASE_URL}/health`, {}, 2000);
      return response.ok;
    } catch {
      return false;
    }
  }

  // Get Metadata
  async getMetadata() {
    try {
      return await this.get('/metadata');
    } catch (error) {
      // Fallback wenn Endpoint nicht existiert
      return { lastUpdate: new Date().toISOString() };
    }
  }

  // ===== ADMIN FUNCTIONS (key-technician only) =====

  // Device Management
  async createDevice(deviceData) {
    return this.post('/admin/devices', deviceData);
  }

  async updateDevice(id, deviceData) {
    return this.put(`/admin/devices/${id}`, deviceData);
  }

  async deleteDevice(id) {
    return this.delete(`/admin/devices/${id}`);
  }

  // Device Image Management
  async uploadDeviceImage(deviceId, imageFile) {
    try {
      const token = await getSessionToken();

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/devices/${deviceId}/image`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData
      }, 60000);

      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error('Device image upload failed:', error);
      throw error;
    }
  }

  /**
   * Bildadresse für Teile, Geräte, Kunden und Endkunden (siehe services/imageCache.js).
   * Online: versionierte Adresse – der Service Worker lädt sie einmal und hält sie offline bereit.
   * Offline: dieselbe Adresse, wenn sie im Cache liegt; sonst Altbestand aus IndexedDB.
   */
  async resolveImageUrl(kind, id, version, legacyStore = null) {
    const token = await getSessionToken();
    const url = token ? buildImageUrl(API_BASE_URL, kind, id, version, token) : null;
    // Ersatzteilbilder im Mobilfunk nur aus dem Offline-Cache (spart Datenvolumen)
    const remoteOk = kind === 'part' ? canLoadRemotePartImages() : navigator.onLine;
    if (url && (remoteOk || await isImageCached(url))) return url;
    if (legacyStore) {
      try {
        const db = await initDatabase();
        const legacy = await db.get(legacyStore, id);
        if (legacy?.blob) return URL.createObjectURL(legacy.blob);
      } catch { /* kein Altbestand */ }
    }
    return null;
  }

  async getDeviceImageUrl(deviceId, version) {
    return this.resolveImageUrl('device', deviceId, version, 'device_images');
  }

  async deleteDeviceImage(deviceId) {
    return this.delete(`/admin/devices/${deviceId}/image`);
  }

  // Customer Image Management
  async getCustomerImageUrl(customerId, version) {
    return this.resolveImageUrl('customer', customerId, version);
  }

  // Subclient (Kunden des Kunden) Management
  async getSubclientLogoUrl(subclientId, version) {
    return this.resolveImageUrl('subclient', subclientId, version);
  }

  async getSubclients(customerId) {
    return this.get(`/customers/${customerId}/subclients`);
  }

  async createSubclient(customerId, formData) {
    return this.postFormData(`/admin/customers/${customerId}/subclients`, formData);
  }

  async updateSubclient(customerId, subclientId, formData) {
    return this.putFormData(`/admin/customers/${customerId}/subclients/${subclientId}`, formData);
  }

  async deleteSubclient(customerId, subclientId) {
    return this.delete(`/admin/customers/${customerId}/subclients/${subclientId}`);
  }

  // Part Management
  async createPart(partData) {
    return this.post('/admin/parts', partData);
  }

  async updatePart(id, partData) {
    return this.put(`/admin/parts/${id}`, partData);
  }

  async deletePart(id) {
    return this.delete(`/admin/parts/${id}`);
  }

  // Part Image Management
  async uploadPartImage(partId, imageFile) {
    try {
      const token = await getSessionToken();

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/parts/${partId}/image`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData
      }, 60000);

      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    }
  }

  async getPartImageUrl(partId, version) {
    if (IS_DEV) return null;
    return this.resolveImageUrl('part', partId, version, 'part_images');
  }

  async deletePartImage(partId) {
    return this.delete(`/admin/parts/${partId}/image`);
  }

  // Part Documentation
  async getPartDocumentation(partId) {
    return this.get(`/parts/${partId}/documentation`);
  }

  async addPartDocumentation(partId, data) {
    try {
      const token = await getSessionToken();
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/parts/${partId}/documentation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }, 30000);
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || 'Fehler beim Hinzufügen');
      }
      
      return await safeJson(response);
    } catch (error) {
      console.error('API POST documentation failed:', error);
      throw error;
    }
  }

  async updatePartDocumentation(partId, docId, data) {
    return this.put(`/admin/parts/${partId}/documentation/${docId}`, data);
  }

  async deletePartDocumentation(partId, docId) {
    return this.delete(`/admin/parts/${partId}/documentation/${docId}`);
  }

  // Device Documentation
  async getDeviceDocumentation(deviceId) {
    return this.get(`/devices/${deviceId}/documentation`);
  }

  async addDeviceDocumentation(deviceId, data) {
    return this.post(`/admin/devices/${deviceId}/documentation`, data);
  }

  async updateDeviceDocumentation(deviceId, docId, data) {
    return this.put(`/admin/devices/${deviceId}/documentation/${docId}`, data);
  }

  async deleteDeviceDocumentation(deviceId, docId) {
    return this.delete(`/admin/devices/${deviceId}/documentation/${docId}`);
  }

  // Document Attachments
  async getDocumentAttachments(docId) {
    return this.get(`/documentation/${docId}/attachments`);
  }

  async uploadDocumentAttachment(docId, file, pdfCategory = null) {
    const formData = new FormData();
    formData.append('file', file);
    if (pdfCategory) formData.append('pdf_category', pdfCategory);
    return this.postFormData(`/admin/documentation/${docId}/attachments`, formData);
  }

  async deleteDocumentAttachment(docId, attachId) {
    return this.delete(`/admin/documentation/${docId}/attachments/${attachId}`);
  }

  async getDocumentationParts(docId) {
    return this.get(`/documentation/${docId}/parts`);
  }

  async linkDocumentationPart(docId, partId) {
    return this.post(`/admin/documentation/${docId}/parts`, { part_id: partId });
  }

  async unlinkDocumentationPart(docId, partId) {
    return this.delete(`/admin/documentation/${docId}/parts/${partId}`);
  }

  async getDocumentationChangelog(docId) {
    return this.get(`/documentation/${docId}/changelog`);
  }

  async notifyDocumentation(docId, triggerType, message) {
    return this.post(`/admin/documentation/${docId}/notify`, { trigger_type: triggerType, message: message || null });
  }

  async getPendingDocNotifications() {
    return this.get('/user/doc-notifications/pending');
  }

  async confirmDocNotification(notifId) {
    return this.post(`/user/doc-notifications/${notifId}/confirm`, {});
  }

  async getDocNotificationStatus(docId) {
    return this.get(`/admin/documentation/${docId}/notification-status`);
  }

  async getDocumentationById(docId) {
    return this.get(`/documentation/${docId}`);
  }

  async getCustomerDocumentation(customerId, subclientId = null) {
    const query = subclientId !== null && subclientId !== undefined ? `?subclient_id=${subclientId}` : '';
    return this.get(`/documentation/customer/${customerId}${query}`);
  }

  async addCustomerDocumentation(customerId, payload) {
    return this.post(`/admin/customers/${customerId}/documentation`, payload);
  }

  async updateCustomerDocumentation(customerId, docId, payload) {
    return this.put(`/admin/customers/${customerId}/documentation/${docId}`, payload);
  }

  async deleteCustomerDocumentation(customerId, docId) {
    return this.delete(`/admin/customers/${customerId}/documentation/${docId}`);
  }

  async getCategoryDocumentation(categoryId) {
    return this.get(`/documentation/category/${categoryId}`);
  }

  async addCategoryDocumentation(categoryId, payload) {
    return this.post(`/admin/categories/${categoryId}/documentation`, payload);
  }

  async updateCategoryDocumentation(categoryId, docId, payload) {
    return this.put(`/admin/categories/${categoryId}/documentation/${docId}`, payload);
  }

  async deleteCategoryDocumentation(categoryId, docId) {
    return this.delete(`/admin/categories/${categoryId}/documentation/${docId}`);
  }

  async approveDocumentation(docId) {
    return this.put(`/admin/documentation/${docId}/approve`, {});
  }

  async submitDocSuggestion(title, description, deviceHint) {
    return this.post('/user/doc-suggestions', { title, description, device_hint: deviceHint || '' });
  }

  async getDocSuggestions() {
    return this.get('/admin/doc-suggestions');
  }

  async updateDocSuggestionStatus(id, status, adminNote) {
    return this.patch(`/admin/doc-suggestions/${id}/status`, { status, admin_note: adminNote || '' });
  }

  async approveDocSuggestion(id, createdDocId = null) {
    return this.post(`/admin/doc-suggestions/${id}/approve`, { created_doc_id: createdDocId });
  }

  async rejectDocSuggestion(id, adminNote = '') {
    return this.post(`/admin/doc-suggestions/${id}/reject`, { admin_note: adminNote || '' });
  }

  async getPendingDocProposals() {
    return this.get('/admin/pending-doc-proposals');
  }

  async getAnalyticsSummary(params = {}) {
    const query = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null)).toString();
    return this.get(`/admin/analytics/summary${query ? `?${query}` : ''}`);
  }

  async getAuditLogs(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/admin/analytics/audit-logs${query ? `?${query}` : ''}`);
  }

  async getPaperlessArchiveDocuments(q = '') {
    const query = q ? `?q=${encodeURIComponent(q)}` : '';
    return this.get(`/admin/paperless/documents${query}`);
  }

  async getPaperlessHealth() {
    return this.get('/admin/paperless/health');
  }

  async fetchAttachmentBlob(attachId) {
    try {
      const token = await getSessionToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_BASE_URL}/documentation/attachments/${attachId}/file`, { headers });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      return { blob, mimetype: response.headers.get('Content-Type') || blob.type };
    } catch (error) {
      console.error('fetchAttachmentBlob failed:', error);
      throw error;
    }
  }

  // User Management
  async getUsers() {
    return this.get('/admin/users');
  }

  async createUser(userData) {
    return this.post('/admin/users', userData);
  }

  async toggleUserActive(userId, isActive) {
    return this.put(`/admin/users/${userId}/active`, { isActive });
  }

  // Nur die Rolle (Benutzerverwaltung); Sonderrechte bleiben erhalten
  async updateUserRole(userId, permissions) {
    return this.put(`/admin/users/${userId}/permissions`, { permissions });
  }

  async updateUserName(userId, firstname, lastname) {
    return this.put(`/admin/users/${userId}/name`, { firstname, lastname });
  }

  async updateUserEmail(userId, email) {
    return this.put(`/admin/users/${userId}/email`, { email });
  }

  async updateUserPassword(userId, newPassword) {
    return this.put(`/admin/users/${userId}/password`, { newPassword });
  }

  async deleteUser(userId) {
    return this.delete(`/admin/users/${userId}`);
  }

  async resetAllUsersAnalyticsConsent() {
    return this.post('/admin/users/reset-analytics-consent', {});
  }

  async getKeyTechnicianEvaluationPdf(userId) {
    try {
      const token = await getSessionToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await this.fetchWithTimeout(
        `${API_BASE_URL}/admin/users/key-technician/${userId}/evaluation/pdf`,
        { headers },
        30000
      );

      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || 'Auswertung konnte nicht geladen werden');
      }

      return await response.blob();
    } catch (error) {
      console.error('API get key-technician evaluation PDF failed:', error);
      throw error;
    }
  }

  async sendKeyTechnicianEvaluationEmail(userId) {
    return this.post(`/admin/users/key-technician/${userId}/evaluation/email`, {});
  }

  async updateUserKeyTechnician(userId, keyTechnicianId) {
    return this.put(`/admin/users/${userId}/key-technician`, { key_technician_id: keyTechnicianId });
  }

  // Database Backup (Admin only) – Export/Import laufen als Hintergrund-Jobs ohne Größenlimit
  async startBackupExport() {
    return this.post('/admin/backup/export', {});
  }

  // Eigenes fetch ohne automatische Abmeldung: während eines Imports kann der Server kurz ablehnen
  async getBackupJob(jobId) {
    const token = await getSessionToken();
    const response = await fetch(`${API_BASE_URL}/admin/backup/jobs/${jobId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    const data = await safeJson(response);
    if (!response.ok) {
      const error = new Error(data?.error || `Status ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return data;
  }

  backupDownloadUrl(downloadPath) {
    return `${API_BASE_URL}${downloadPath}`;
  }

  // Upload per XMLHttpRequest: Fortschrittsanzeige und kein Zeitlimit (beliebig große Dateien)
  async uploadBackupForRestore(file, onProgress) {
    const token = await getSessionToken();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/admin/backup/restore`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
      };
      xhr.onload = () => {
        let data = null;
        try { data = JSON.parse(xhr.responseText); } catch { /* keine JSON-Antwort */ }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data?.error || `Upload fehlgeschlagen (Status ${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Upload fehlgeschlagen – Verbindung unterbrochen'));
      const formData = new FormData();
      formData.append('backup', file);
      xhr.send(formData);
    });
  }

  // Generic FormData methods
  async postFormData(endpoint, formData, timeout = 60000) {
    try {
      const token = await getSessionToken();
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData
      }, timeout);
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API POST FormData ${endpoint} failed:`, error);
      throw error;
    }
  }

  async putFormData(endpoint, formData, timeout = 60000) {
    try {
      const token = await getSessionToken();
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await this.fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers,
        body: formData
      }, timeout);
      
      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }
      return await safeJson(response);
    } catch (error) {
      console.error(`API PUT FormData ${endpoint} failed:`, error);
      throw error;
    }
  }

  getBaseURL() {
    return API_BASE_URL;
  }

  // ==================== FAVORITEN-METHODEN ====================

  async getFavorites() {
    try {
      return await this.get('/favorites');
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
      throw error;
    }
  }

  async addFavorite(partId) {
    try {
      return await this.post(`/favorites/${partId}`, {});
    } catch (error) {
      console.error('Failed to add favorite:', error);
      throw error;
    }
  }

  async removeFavorite(partId) {
    try {
      return await this.delete(`/favorites/${partId}`);
    } catch (error) {
      console.error('Failed to remove favorite:', error);
      throw error;
    }
  }

  async exportFavorites() {
    try {
      const token = await getSessionToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await this.fetchWithTimeout(`${API_BASE_URL}/favorites/export`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await safeJson(response);
    } catch (error) {
      console.error('Failed to export favorites:', error);
      throw error;
    }
  }

  async importFavorites(favoritesData, replaceExisting = false) {
    try {
      return await this.post('/favorites/import', {
        favorites: favoritesData,
        replaceExisting
      });
    } catch (error) {
      console.error('Failed to import favorites:', error);
      throw error;
    }
  }

  // Exclusive Locks & History
  async acquireDocLock(docId) {
    return this.post(`/documentation/${docId}/lock`, {});
  }

  async releaseDocLock(docId) {
    return this.post(`/documentation/${docId}/unlock`, {});
  }

  async getDocVersions(docId) {
    return this.get(`/documentation/${docId}/versions`);
  }

  async restoreDocVersion(docId, versionId) {
    return this.post(`/admin/documentation/${docId}/versions/${versionId}/restore`, {});
  }

  // Document Templates
  async getDocTemplates() {
    return this.get('/documentation/templates');
  }

  async createDocTemplate(data) {
    if (data instanceof FormData) {
      return this.postMultipart('/admin/documentation/templates', data);
    }
    return this.post('/admin/documentation/templates', data);
  }

  async updateDocTemplate(id, data) {
    if (data instanceof FormData) {
      return this.putMultipart(`/admin/documentation/templates/${id}`, data);
    }
    return this.put(`/admin/documentation/templates/${id}`, data);
  }

  async deleteDocTemplate(id) {
    return this.delete(`/admin/documentation/templates/${id}`);
  }

  async generateDocFromTemplate(targetType, targetId, payload) {
    return this.post(`/admin/documentation/generate`, {
      ...payload,
      target_type: targetType,
      target_id: targetId
    });
  }

  async getOnlyOfficeConfig(docId) {
    return this.get(`/admin/documentation/${docId}/onlyoffice-config`);
  }

  async forceSaveOnlyOffice(docId, key) {
    return this.post(`/admin/documentation/${docId}/forcesave`, { key });
  }

  // Permissions Management
  async getPermissions() {
    return this.get('/admin/permissions');
  }

  async updateGroupPermissions(groupPermissions) {
    return this.put('/admin/permissions/groups', { groupPermissions });
  }

  async updateUserPermissions(userId, role, customPermissions) {
    return this.put(`/admin/permissions/users/${userId}`, { role, customPermissions });
  }
}

export const apiService = new ApiService();
export default apiService;
